import React, { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useGame } from "@/context/GameContext";
import { BUSINESSES } from "@/data/gameData";
import { startCheckout, restorePurchase } from "@/lib/payments";
import { Sound } from "@/lib/sound";
import { Lock, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import PromoCodeForm from "@/components/PromoCodeForm";

const PaywallModal = () => {
  const { state, paywallOpen, setPaywallOpen, markPaywallSeen, setUnlocked } = useGame();
  const [loading, setLoading] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [restoring, setRestoring] = useState(false);
  const locked = Object.values(BUSINESSES).filter((b) => b.id !== "dog_walker");

  const close = () => { markPaywallSeen(); setPaywallOpen(false); };

  const pay = async () => {
    setLoading(true);
    try {
      const { checkout_url } = await startCheckout();
      window.location.href = checkout_url;
    } catch (e) {
      setLoading(false);
      toast.error("Couldn't open checkout. Please try again.");
    }
  };

  const restore = async () => {
    if (!email.trim()) return;
    setRestoring(true);
    try {
      const res = await restorePurchase(email.trim());
      if (res.unlocked) {
        Sound.fanfare();
        setUnlocked(res.email_hint, res);
        toast.success("Purchase restored — all businesses unlocked!");
        setPaywallOpen(false);
      } else {
        toast.error("No purchase found for that email.");
      }
    } catch (e) {
      toast.error(e?.response?.status === 429 ? "Too many tries — please wait 15 minutes." : "Please enter a valid email.");
    }
    setRestoring(false);
  };

  return (
    <Dialog open={paywallOpen && !state.unlocked} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-lg rounded-3xl border-4 border-amber-400 bg-white p-0 overflow-hidden" data-testid="paywall-modal">
        <div className="bg-gradient-to-br from-amber-300 to-orange-400 p-6 text-center">
          <div className="text-6xl mb-1 animate-bounce-slow">🔓</div>
          <div className="text-[10px] font-black uppercase tracking-[0.3em] text-orange-900/80">Grown-ups only</div>
          <h2 className="font-display text-3xl font-bold text-slate-900 leading-tight">Unlock all 4 businesses</h2>
          <p className="text-sm font-heading font-bold text-slate-800 mt-1">
            {state.playerName} finished the first job! Ask a parent to unlock the rest.
          </p>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-2">
            {locked.map((b) => (
              <div key={b.id} className={`${b.lightBg} border-2 ${b.borderColor} rounded-2xl p-2 text-center`} data-testid={`paywall-biz-${b.id}`}>
                <div className="text-3xl">{b.emoji}</div>
                <div className="font-heading font-black text-xs text-slate-800">{b.name}</div>
              </div>
            ))}
          </div>
          <ul className="text-sm font-heading font-bold text-slate-700 space-y-1">
            <li className="flex gap-2"><Sparkles className="w-4 h-4 text-amber-500 shrink-0" /> 3 more jobs, mini-games & customers</li>
            <li className="flex gap-2"><Sparkles className="w-4 h-4 text-amber-500 shrink-0" /> All lessons, flyers & the Top Boss race</li>
            <li className="flex gap-2"><ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" /> One-time payment · no subscription · no ads</li>
          </ul>

          <Button
            onClick={pay}
            disabled={loading}
            data-testid="paywall-pay-btn"
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-heading font-black text-lg py-7 rounded-2xl btn-3d border-2 border-emerald-600"
          >
            <Lock className="w-5 h-5 mr-2" /> {loading ? "Opening secure checkout…" : "Parent: unlock for $8.99"}
          </Button>
          <p className="text-[11px] text-slate-500 text-center font-semibold">Secure payment by Stripe. Unlock is saved to the parent's email so it works on any device.</p>

          {restoreOpen ? (
            <div className="flex gap-2" data-testid="restore-form">
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="parent@email.com" type="email" className="rounded-xl border-2 border-slate-300 font-bold" data-testid="restore-email-input" onKeyDown={(e) => e.key === "Enter" && restore()} />
              <Button onClick={restore} disabled={restoring} data-testid="restore-submit-btn" className="bg-slate-800 hover:bg-slate-900 text-white font-heading font-black rounded-xl">
                {restoring ? "…" : "Restore"}
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap justify-between gap-2 text-xs font-black uppercase tracking-wider">
              <button onClick={() => setRestoreOpen(true)} data-testid="restore-purchase-btn" className="text-blue-600 hover:underline">Already paid? Restore</button>
              <PromoCodeForm />
              <button onClick={close} data-testid="paywall-not-now-btn" className="text-slate-400 hover:text-slate-600">Not now — keep dog walking</button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaywallModal;
